import * as Yup from "yup";
import { RRule } from "rrule";

import type {
  AccountAlertForm,
  BaseAlertForm,
  TransactionAlertForm,
} from "./form";
import type { TypeSafeReactLocalization } from "../../../composites";

const isValidRecurrenceRule = (rruleStr?: string) => {
  try {
    if (!rruleStr) return false;
    return !!RRule.fromString(rruleStr);
  } catch (error) {
    return false;
  }
};

export enum AlertDeliveryChannel {
  EMAIL = "email",
  PUSH = "push",
  SMS = "sms",
}

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const baseValidationSchema = ({
  getString,
}: TypeSafeReactLocalization) =>
  Yup.object<BaseAlertForm>({
    alertKey: Yup.string()
      .required(getString("form-generic-validation-required"))
      .notOneOf(["unknown"], getString("form-generic-validation-required")),
    deliveryChannels: Yup.array()
      .of(
        Yup.string().oneOf(
          Object.values(AlertDeliveryChannel),
          getString("alerts-form-error-delivery-method"),
        ),
      )
      .min(1, getString("alerts-form-error-delivery-method"))
      .test(
        "unique-delivery-channels",
        getString("alerts-form-error-delivery-method"),
        (value) => !value || value.length === new Set(value).size,
      )
      .required(),
  });

export const transactionAlertValidationSchema = (
  l10n: TypeSafeReactLocalization,
): Yup.ObjectSchema<Yup.AnyObject, TransactionAlertForm> =>
  baseValidationSchema(l10n).concat(
    Yup.object({
      transactionQuery: Yup.string()
        .required(l10n.getString("form-generic-validation-required"))
        .test(
          "is-valid-query",
          l10n.getString("form-generic-validation-required"),
          (value) => {
            if (!value) return false; // If no value, skip validation
            if (value === "0") {
              return false;
            }
            return true;
          },
        ),
    }),
  );

export const accountAlertValidationSchema = (
  l10n: TypeSafeReactLocalization,
): Yup.ObjectSchema<Yup.AnyObject, AccountAlertForm> =>
  baseValidationSchema(l10n).concat(
    Yup.object({
      rrule: Yup.string()
        .nullable()
        // If alertKey is "available_balance", rrule is required.
        .when("alertKey", {
          is: "available_balance",
          then: (schema) =>
            schema.required(l10n.getString("form-generic-validation-required")),
          otherwise: (schema) => schema.nullable(),
        })
        // Custom test: if a value is provided, it must be a valid recurrence rule.
        .test(
          "is-valid-rrule",
          l10n.getString("alerts-form-error-rrule"),
          (value) => value === null || isValidRecurrenceRule(value),
        ),
      thresholdAmount: Yup.number()
        .nullable()
        // If alertKey is "low_available_balance", thresholdAmount is required.
        .when("alertKey", {
          is: "low_available_balance",
          then: (schema) =>
            schema
              .required(l10n.getString("form-generic-validation-required"))
              .min(1, l10n.getString("alerts-form-error-threshold-amount")),
          otherwise: (schema) => schema.nullable(),
        }),
      subscribedAccounts: Yup.array()
        .of(
          Yup.string().matches(
            UUID_REGEX,
            l10n.getString("alerts-form-error-subscribed-accounts"),
          ),
        )
        .min(1, l10n.getString("alerts-form-error-subscribed-accounts"))
        .required(l10n.getString("alerts-form-error-subscribed-accounts")),
    }),
  );

export const loanAlertValidationSchema = (
  l10n: TypeSafeReactLocalization,
): Yup.ObjectSchema<Yup.AnyObject, AccountAlertForm> =>
  baseValidationSchema(l10n).concat(
    Yup.object({
      reminderDaysOffset: Yup.number()
        .nullable()
        .when("alertKey", {
          // If alertKey is "loan_reminder", reminderDaysOffset is required.
          is: "loan_reminder",
          then: (schema) =>
            schema
              .required(
                l10n.getString("alerts-form-error-reminder-days-offset"),
              )
              .min(0, l10n.getString("alerts-form-error-reminder-days-offset"))
              .max(7, l10n.getString("alerts-form-error-reminder-days-offset")),
          otherwise: (schema) => schema.nullable(),
        }),
    }),
  );
